import { memo } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Heading, HStack, Text, VStack } from '@library';
import Divider from '../../../../core/components/Divider';
import ItemDiscountTag from '../../../../core/components/ItemDiscountTag';
import HorizontalLayout from '../../../../core/components/layout/HorizontalLayout';
import usePriceFormatter from '../../../../core/hooks/usePriceFormatter';
import type { LoggedInProductCustomerPrice } from '../../../../product/types';
import styles from './PreorderProductPriceBreakdown.module.scss';

const PreorderPriceBreakdownSection = memo(
    ({
        title,
        subtitle,
        price,
        discount,
        isPreorderDiscount = false,
        hideDiscountRow = false,
    }: {
        title: ReactNode;
        subtitle: ReactNode;
        price: number;
        discount?: number;
        isPreorderDiscount?: boolean;
        hideDiscountRow?: boolean;
    }) => {
        const priceFormatter = usePriceFormatter();
        const { currency, price: formattedPrice } = priceFormatter(price);
        const { price: formattedDiscount } = priceFormatter(
            (price * (discount || 0)) / 100
        );

        const shouldShowDiscount =
            Math.abs(discount ?? 0) > 0 && !hideDiscountRow;

        return (
            <>
                <div className={styles.preorderProductPriceBreakdownContent}>
                    <VStack gap="micro">
                        <Text primary>{title}</Text>
                        {shouldShowDiscount && (
                            <Text primary light>
                                {subtitle}
                            </Text>
                        )}
                    </VStack>
                </div>
                <div className={styles.preorderProductPriceBreakdownContent}>
                    <VStack gap="micro">
                        <Text primary>
                            {currency}&nbsp;{formattedPrice}
                        </Text>
                        {shouldShowDiscount && (
                            <HorizontalLayout align="center">
                                <ItemDiscountTag
                                    dealerDiscountPercentage={
                                        isPreorderDiscount ? 0 : discount!
                                    }
                                    additionalDiscountPercentage={
                                        isPreorderDiscount ? discount! : 0
                                    }
                                    onSale={isPreorderDiscount}
                                />
                                &nbsp;
                                <Text primary light>
                                    ({currency}&nbsp;
                                    {formattedDiscount})
                                </Text>
                            </HorizontalLayout>
                        )}
                    </VStack>
                </div>
            </>
        );
    }
);

const PreorderProductPriceBreakdown = memo(
    ({ productPrice }: { productPrice: LoggedInProductCustomerPrice }) => {
        const formatPrice = usePriceFormatter();

        if (!productPrice.finalPrice) {
            return null;
        }

        const { currency, price } = formatPrice(productPrice.finalPrice);
        const isSpecialDiscountApplied =
            productPrice.appliedDiscountRule === 'specialPrice';

        const dealerPrice =
            (productPrice.generalDealerPrice *
                (100 - productPrice.dealerDiscountPercentage || 0)) /
            100;

        const additionalDiscountPercentage =
            productPrice.additionalDiscountPercentage || 0;

        return (
            <div className={styles.preorderProductPriceBreakdown}>
                <div className={styles.preorderProductPriceBreakdownTitle}>
                    <Heading h3 bold>
                        Preisinformation
                    </Heading>
                </div>
                <PreorderPriceBreakdownSection
                    title={<FormattedMessage id="base price" />}
                    subtitle={<FormattedMessage id="less dealer discount" />}
                    price={productPrice.generalDealerPrice}
                    discount={productPrice.dealerDiscountPercentage}
                    hideDiscountRow={isSpecialDiscountApplied}
                />
                <div className={styles.sectionDivider}>
                    <Divider light lengthPercentage={100} />
                </div>
                {additionalDiscountPercentage > 0 && (
                    <>
                        <PreorderPriceBreakdownSection
                            title={<FormattedMessage id="dealer price" />}
                            subtitle={
                                <FormattedMessage id="less additional discount" />
                            }
                            price={dealerPrice}
                            discount={additionalDiscountPercentage}
                            isPreorderDiscount
                        />
                        <div className={styles.sectionDivider}>
                            <Divider light lengthPercentage={100} />
                        </div>
                    </>
                )}

                <div className={styles.preorderProductPriceBreakdownContent}>
                    <HStack align="center">
                        <Text primary bold>
                            <FormattedMessage id="my dealer price" />
                        </Text>
                        &nbsp;&nbsp;
                        {isSpecialDiscountApplied && (
                            <ItemDiscountTag
                                onSale
                                isSpecial={isSpecialDiscountApplied}
                                dealerDiscountPercentage={0}
                            />
                        )}
                    </HStack>
                </div>
                <div className={styles.preorderProductPriceBreakdownContent}>
                    <Text primary bold>
                        {currency}&nbsp;{price}
                    </Text>
                </div>
            </div>
        );
    }
);

export default memo(PreorderProductPriceBreakdown);
